import ExcelJS from "exceljs";
import React, { useState } from "react";
import { MdAdd } from "react-icons/md";
import Modal from "../../../../components/ModalLib";

import { ContainerSubtitle, RowNone, TabletSelect } from "./styles";

import { FaTrashAlt } from "react-icons/fa";
import { FiDownload, FiEdit, FiTrash2, FiUpload } from "react-icons/fi";
import { useToasts } from "react-toast-notifications";
import { Search } from "../../../../components/Search";
import { PopUpConfirmation } from "../../styles";
import { ModalEditException } from "./components/ModalEditException";
import { ModalImportException } from "./components/ModalImportException";

interface ExceptionScheduling {
  nf: string;
  reason?: string;
  obs?: string;
  scheduling_exception_reasonReason?: {
    resposable?: true;
  };
}

interface SelectExceptionsProps {
  onSaveChanges?: () => void;
  onChangeExceptions: (data: ExceptionScheduling[]) => void;
  exceptions: ExceptionScheduling[];
  exceptionsSaved: ExceptionScheduling[];
  disabled?: boolean;
  exceptionReasons: { value: string; name: string }[];
  style?: {
    maxHeight?: string;
  };
}

export const SelectExceptions: React.FC<SelectExceptionsProps> = ({
  onChangeExceptions,
  exceptions,
  disabled = false,
  exceptionReasons,
  style,
  exceptionsSaved,
  onSaveChanges,
}) => {
  const { addToast } = useToasts();

  const [modalVisibleNewException, setModalVisibleNewException] =
    useState(false);
  const [modalVisibleImportException, setModalVisibleImportException] =
    useState(false);
  const [confirmDeleteException, setConfirmDeleteException] =
    useState<ExceptionScheduling>();
  const [modalUpdateException, setModalUpdateException] =
    useState<ExceptionScheduling>();

  const [searchCurrentException, setSearchCurrentException] = useState("");

  function handleSelect(exception: ExceptionScheduling) {
    const alreadySelected = exceptions.find((f) => f.nf === exception.nf);

    if (!alreadySelected) {
      onChangeExceptions([...exceptions, exception]);
    }

    setModalVisibleNewException(false);
  }

  function areExceptionsEqual(
    a: ExceptionScheduling[],
    b: ExceptionScheduling[]
  ) {
    if (a.length !== b.length) return false;

    const sortedA = [...a].sort((x, y) => x.nf.localeCompare(y.nf));
    const sortedB = [...b].sort((x, y) => x.nf.localeCompare(y.nf));

    return sortedA.every(
      (item, index) =>
        item.nf === sortedB[index].nf &&
        item.reason === sortedB[index].reason &&
        item.obs === sortedB[index].obs
    );
  }

  function handleRemove(exception: ExceptionScheduling) {
    const filterExceptionRemove = exceptions.filter(
      (f) => f.nf !== exception.nf
    );
    setConfirmDeleteException(undefined);

    onChangeExceptions(filterExceptionRemove);
  }

  function handleUpdate(exceptionUpdated: ExceptionScheduling) {
    setModalUpdateException(undefined);

    onChangeExceptions(
      exceptions.map((exception) =>
        exception.nf === exceptionUpdated.nf ? exceptionUpdated : exception
      )
    );
  }

  async function handleExportExceptions() {
    try {
      if (exceptions.length === 0) {
        return addToast("Nenhuma exceção para exportar.", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Exceções");

      worksheet.columns = [
        { header: "Nota Fiscal", key: "nf", width: 20 },
        { header: "Motivo", key: "reason", width: 40 },
      ];

      exceptions.forEach((item) => worksheet.addRow(item));

      workbook.xlsx.writeBuffer().then(function (data) {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `ressalvas.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
    } catch (error) {
      return addToast(
        "Ocorreu um erro ao gerar arquivo, Tente novamente mais tarde ou entre em contato conosco (51) 3441-1000",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    }
  }

  async function handleUpdateMany(exceptionsImport: ExceptionScheduling[]) {
    setModalVisibleImportException(false);

    const normalized = [...exceptions];

    for (const item of exceptionsImport) {
      try {
        const find = normalized.find((f) => Number(f.nf) === Number(item.nf));
        const normalizedReason = item.reason
          ?.trim()
          .replace(/\(BACKOFFICE\)|\(LOGISTICO\)/g, "")
          .trim();

        if (!!find) {
          find.reason = normalizedReason;
          find.obs = item.obs;
        } else {
          normalized.push({
            ...item,
            nf: String(item.nf),
            reason:
              normalizedReason &&
              exceptionReasons
                .map((item) => item.value)
                .includes(normalizedReason)
                ? normalizedReason
                : undefined,
          });
        }
      } catch (error) {
        console.error("Erro ao atualizar exceções:", error);
      }
    }

    onChangeExceptions(normalized);
  }

  function searchExceptions(search: string): ExceptionScheduling[] {
    return !!searchCurrentException
      ? exceptions.filter(
          (exception) =>
            exception.nf.toLowerCase().includes(search.toLowerCase()) ||
            exception.reason?.toLowerCase().includes(search.toLowerCase()) ||
            exception.obs?.toLowerCase().includes(search.toLowerCase())
        )
      : exceptions;
  }

  const currentExceptionFilterApplied = searchExceptions(
    searchCurrentException
  ).sort((a, b) => a.nf.localeCompare(b.nf));

  const hasChanges = !areExceptionsEqual(exceptions, exceptionsSaved);

  return (
    <>
      <ContainerSubtitle>
        <span>Ressalvas ({exceptions.length})</span>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          {!disabled && (
            <>
              <button
                type="button"
                disabled={!hasChanges}
                className={`action-button ${!hasChanges ? "disabled" : ""}`}
                onClick={onSaveChanges}
                style={{
                  backgroundColor: hasChanges ? "#518de6" : undefined,
                  color: hasChanges ? "#fff" : undefined,
                }}
              >
                Salvar alterações
              </button>
              <button
                type="button"
                className="action-button"
                onClick={() => {
                  setModalVisibleNewException(true);
                }}
              >
                <MdAdd />
                <span>Adicionar</span>
              </button>
              <button
                type="button"
                className="action-button"
                onClick={() => {
                  setModalVisibleImportException(true);
                }}
              >
                <FiUpload />
                <span>Importar</span>
              </button>
            </>
          )}

          <button
            type="button"
            className="action-button"
            onClick={handleExportExceptions}
          >
            <FiDownload />
            <span>Exportar</span>
          </button>
        </div>
      </ContainerSubtitle>

      <Search
        currentSearch={searchCurrentException}
        handleSearch={setSearchCurrentException}
      />

      <div style={{ maxHeight: style?.maxHeight ?? "18rem", overflow: "auto" }}>
        <TabletSelect>
          <thead>
            <tr>
              {!disabled && <th>Ações</th>}
              <th>Nota Fiscal</th>
              <th>Motivo</th>
              <th>Observação</th>
            </tr>
          </thead>

          <tbody>
            {currentExceptionFilterApplied.map((exception) => (
              <tr key={exception.nf}>
                {!disabled && (
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          setConfirmDeleteException(exception);
                        }}
                      >
                        <FiTrash2 size="1.2rem" color="#555" />
                      </button>
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => {
                          setModalUpdateException(exception);
                        }}
                      >
                        <FiEdit size="1.2rem" color="#555" />
                      </button>
                    </div>
                  </td>
                )}

                <td>{exception.nf}</td>
                <td>
                  {exception.reason} (
                  {exception?.scheduling_exception_reasonReason?.resposable})
                </td>
                <td>{exception.obs}</td>
              </tr>
            ))}
          </tbody>
        </TabletSelect>
      </div>

      {currentExceptionFilterApplied.length < 1 && (
        <RowNone>Não há registros a serem exibidos</RowNone>
      )}

      <Modal
        modalVisible={!!confirmDeleteException}
        setModalVisible={() => {
          setConfirmDeleteException(undefined);
        }}
        headerOff={true}
        height={350}
        maxWidth={650}
      >
        <PopUpConfirmation>
          <div className="headerPopUp">
            <nav>
              <FaTrashAlt size={95} opacity={0.2} />
            </nav>
            <span>Você tem certeza que deseja excluir?</span>
          </div>
          <div className="buttonsPopUp">
            <button
              type="button"
              onClick={() => setConfirmDeleteException(undefined)}
            >
              Cancelar
            </button>
            <button
              style={{ background: "#d21e26" }}
              type="button"
              onClick={() => {
                if (confirmDeleteException) {
                  handleRemove(confirmDeleteException);
                }
              }}
            >
              Deletar
            </button>
          </div>
        </PopUpConfirmation>
      </Modal>

      <Modal
        modalVisible={!!modalUpdateException}
        setModalVisible={() => {
          setModalUpdateException(undefined);
        }}
        headerOff={true}
        height={350}
        maxWidth={650}
      >
        {!!modalUpdateException && (
          <ModalEditException
            data={modalUpdateException}
            exceptionReasons={exceptionReasons}
            onChange={handleUpdate}
          />
        )}
      </Modal>

      <Modal
        modalVisible={modalVisibleNewException}
        setModalVisible={() => {
          setModalVisibleNewException(false);
        }}
        headerOff={true}
        height={350}
        maxWidth={650}
      >
        <ModalEditException
          data={{ nf: "", reason: "" }}
          exceptionReasons={exceptionReasons}
          onChange={handleSelect}
          isNew={true}
        />
      </Modal>

      <Modal
        modalVisible={modalVisibleImportException}
        setModalVisible={() => {
          setModalVisibleImportException(false);
        }}
        headerOff={true}
        height={350}
        maxWidth={650}
      >
        <ModalImportException
          onChange={(importExceptions) => {
            handleUpdateMany(importExceptions);
          }}
        />
      </Modal>
    </>
  );
};
